import { useState } from "react";
import { Accordion, Alert, Button, Card, Col, Collapse, Form, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import type {
    IListSourceExtractionFieldResult,
    IWasteReturnTemplateMapping,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { WasteReturnRegFieldKeys } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { buildListSourceFieldSummary } from "./listExtractionDisplay";
import {
    MAPPING_ADDRESS_FIELD_LABEL,
    MAPPING_ADDRESS_FIELD_PLACEHOLDER,
    MAPPING_ADDRESS_FIELD_TOOLTIP,
} from "./mappingAddressFieldLabels";
import { GEOGRAPHY_ADJACENT_COLUMNS, REG_FIELD_KEYS_FOR_COMPARISON } from "./wasteReturnTemplateMappingDefaults";

const FIELD_LABELS: Record<string, string> = {
    [WasteReturnRegFieldKeys.Ewc]: "EWC",
    [WasteReturnRegFieldKeys.EaDrReceived]: "D/R received",
    [WasteReturnRegFieldKeys.EaDrRemoved]: "D/R removed",
    [WasteReturnRegFieldKeys.PhysicalState]: "Physical state",
    [WasteReturnRegFieldKeys.SourceActivity]: "Source activity",
    [WasteReturnRegFieldKeys.PreTreatment]: "Pre-treatment",
    [WasteReturnRegFieldKeys.Geography]: "Geography",
    [WasteReturnRegFieldKeys.FacilityOperation]: "Facility operation",
};

export interface RegDataUnresolvedSourcesPanelProps {
    mapping: IWasteReturnTemplateMapping;
    sheetOptions: string[];
    extractionFields: Record<string, IListSourceExtractionFieldResult>;
    resolvingFieldKey: string | null;
    disabled?: boolean;
    onResolve: (fieldKey: string, sheet: string, valueRange: string, adjacentColumns?: Record<string, string>) => void;
}

function isFieldUnresolved(
    fieldKey: string,
    mapping: IWasteReturnTemplateMapping,
    extractionFields: Record<string, IListSourceExtractionFieldResult>
): boolean {
    const listSource = mapping.listSources[fieldKey];
    const summary = buildListSourceFieldSummary(fieldKey, listSource, extractionFields[fieldKey]);
    if (summary.extractedCount > 0) return false;
    if (summary.warningCount > 0) return true;
    return summary.extractedCount === 0;
}

interface UnresolvedSourceItemProps {
    fieldKey: string;
    mapping: IWasteReturnTemplateMapping;
    sheetOptions: string[];
    extractionFields: Record<string, IListSourceExtractionFieldResult>;
    resolvingFieldKey: string | null;
    disabled?: boolean;
    initialSheet: string;
    initialAddress: string;
    initialAdjacent: Record<string, string>;
    onResolve: RegDataUnresolvedSourcesPanelProps["onResolve"];
}

function UnresolvedSourceItem({
    fieldKey,
    mapping,
    sheetOptions,
    extractionFields,
    resolvingFieldKey,
    disabled,
    initialSheet,
    initialAddress,
    initialAdjacent,
    onResolve,
}: UnresolvedSourceItemProps) {
    const [sheet, setSheet] = useState(initialSheet);
    const [address, setAddress] = useState(initialAddress);
    const [adjacent, setAdjacent] = useState(initialAdjacent);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const fieldResult = extractionFields[fieldKey];
    const summary = buildListSourceFieldSummary(fieldKey, mapping.listSources[fieldKey], fieldResult);
    const isGeography = fieldKey === WasteReturnRegFieldKeys.Geography;

    return (
        <Accordion.Item eventKey={fieldKey}>
            <Accordion.Header>
                {FIELD_LABELS[fieldKey] ?? fieldKey}
                {summary.warningCount > 0 && (
                    <span className="ms-2 badge bg-warning text-dark">{summary.warningCount} warning(s)</span>
                )}
            </Accordion.Header>
            <Accordion.Body>
                {summary.warnings.length > 0 && (
                    <Alert variant="warning" className="py-2 small">
                        {summary.warnings.map((warning, index) => (
                            <div key={`${warning.code}-${index}`}>
                                <strong>{warning.code}</strong>: {warning.message}
                            </div>
                        ))}
                    </Alert>
                )}

                <Row className="g-3 align-items-end">
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Worksheet</Form.Label>
                            <Form.Select value={sheet} disabled={disabled} onChange={(e) => setSheet(e.target.value)}>
                                <option value="">Select sheet…</option>
                                {sheetOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>
                                <OverlayTrigger placement="top" overlay={<Tooltip>{MAPPING_ADDRESS_FIELD_TOOLTIP}</Tooltip>}>
                                    <span className="text-decoration-underline text-decoration-style-dotted cursor-help">
                                        {MAPPING_ADDRESS_FIELD_LABEL}
                                    </span>
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                value={address}
                                disabled={disabled}
                                placeholder={MAPPING_ADDRESS_FIELD_PLACEHOLDER}
                                onChange={(e) => setAddress(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md="auto">
                        <Button
                            variant="primary"
                            disabled={disabled || !sheet.trim() || !address.trim() || resolvingFieldKey === fieldKey}
                            onClick={() => onResolve(fieldKey, sheet.trim(), address.trim(), isGeography ? adjacent : undefined)}
                        >
                            {resolvingFieldKey === fieldKey ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Resolving…
                                </>
                            ) : (
                                "Resolve"
                            )}
                        </Button>
                    </Col>
                </Row>

                {isGeography && (
                    <>
                        <Button
                            variant="link"
                            className="px-0 mt-2"
                            onClick={() => setShowAdvanced((open) => !open)}
                            aria-expanded={showAdvanced}
                        >
                            Advanced column mapping
                        </Button>
                        <Collapse in={showAdvanced}>
                            <Row className="g-3 mt-1">
                                {GEOGRAPHY_ADJACENT_COLUMNS.map((columnKey) => (
                                    <Col md={4} key={columnKey}>
                                        <Form.Group>
                                            <Form.Label className="text-capitalize">{columnKey} column</Form.Label>
                                            <Form.Control
                                                value={adjacent[columnKey] ?? ""}
                                                disabled={disabled}
                                                onChange={(e) =>
                                                    setAdjacent((current) => ({
                                                        ...current,
                                                        [columnKey]: e.target.value.toUpperCase(),
                                                    }))
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                ))}
                            </Row>
                        </Collapse>
                    </>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
}

export function RegDataUnresolvedSourcesPanel({
    mapping,
    sheetOptions,
    extractionFields,
    resolvingFieldKey,
    disabled,
    onResolve,
}: RegDataUnresolvedSourcesPanelProps) {
    const unresolvedKeys = REG_FIELD_KEYS_FOR_COMPARISON.filter((fieldKey) =>
        isFieldUnresolved(fieldKey, mapping, extractionFields)
    );

    if (unresolvedKeys.length === 0) {
        return null;
    }

    return (
        <Card className="mb-4 border-warning">
            <Card.Header>List sources that need your help</Card.Header>
            <Card.Body>
                <p className="text-muted small">
                    We could not read dropdown values for these fields from the workbook. Point us at the list range, then click Resolve to extract values before aligning regulatory data.
                </p>

                <Accordion alwaysOpen>
                    {unresolvedKeys.map((fieldKey) => {
                        const manual = mapping.listSources[fieldKey]?.manualOverride;
                        return (
                            <UnresolvedSourceItem
                                key={fieldKey}
                                fieldKey={fieldKey}
                                mapping={mapping}
                                sheetOptions={sheetOptions}
                                extractionFields={extractionFields}
                                resolvingFieldKey={resolvingFieldKey}
                                disabled={disabled}
                                initialSheet={manual?.sheet ?? ""}
                                initialAddress={manual?.valueRange ?? ""}
                                initialAdjacent={{ ...(manual?.adjacentColumnMap ?? {}) }}
                                onResolve={onResolve}
                            />
                        );
                    })}
                </Accordion>
            </Card.Body>
        </Card>
    );
}
